import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  List,
  ListItem,
  ListItemText,
  Button,
  IconButton,
  CircularProgress,
  Box,
} from '@mui/material';
import { Search, Close } from '@mui/icons-material';
import { plainToClass } from 'class-transformer';
import { getClients } from 'api/picking/get-clients';
import { ClientVm } from 'api/picking/client.vm';

interface CustomerSelectionDialogProps {
  open: boolean;
  onClose: () => void;
  onSelect: (clientId: string, clientName: string) => void;
}

const CustomerSelectionDialog: React.FC<CustomerSelectionDialogProps> = ({ open, onClose, onSelect }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [clients, setClients] = useState<ClientVm[]>([]);
  const [loading, setLoading] = useState(false);
  const [nextOffset, setNextOffset] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);

  const fetchClients = (offset: string | null = null, reset: boolean = false) => {
    setLoading(true);

    getClients(offset || '', searchTerm, {
      success: (response) => {
        const clientVms = plainToClass(ClientVm, response.objects);
        setHasMore(response.nextOffset !== null && response.nextOffset !== '');
        setNextOffset(response.nextOffset);
        setClients(reset ? clientVms : [...clients, ...clientVms]);
        setLoading(false);
      },
      error: (err) => {
        console.error('Falha ao obter clientes:', err);
        setLoading(false);
      },
    });
  };

  useEffect(() => {
    if (open) {
      fetchClients(null, true);
    }
  }, [open, searchTerm]);

  const handleLoadMore = () => {
    if (!hasMore || !nextOffset) return;
    fetchClients(nextOffset);
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          Selecionar Cliente
          <IconButton onClick={onClose}>
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <TextField
          fullWidth
          margin="normal"
          placeholder="Procurar clientes..."
          InputProps={{
            startAdornment: <Search sx={{ color: 'action.active', mr: 1 }} />,
          }}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <List>
          {clients.map((client) => (
            <ListItem
              component="button"
              key={client.id}
              onClick={() => onSelect(client.id, client.name)}
              sx={{
                '&:hover': {
                  backgroundColor: 'action.hover',
                  cursor: 'pointer',
                },
              }}
            >
              <ListItemText primary={client.name} secondary={`Criado em: ${new Date(client.createdAt).toLocaleDateString('pt-PT')}`} />
            </ListItem>
          ))}
        </List>
        {loading && (
          <Box display="flex" justifyContent="center">
            <CircularProgress size={24} />
          </Box>
        )}
        {hasMore && !loading && (
          <Box display="flex" justifyContent="center" mt={2}>
            <Button variant="outlined" onClick={handleLoadMore}>
              Carregar mais
            </Button>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
      </DialogActions>
    </Dialog>
  );
};

export default CustomerSelectionDialog;
